import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export default function RnrConversionModal({ isOpen, onClose, onSuccess }) {
  const { t } = useTranslation('app');
  const [rate, setRate] = useState(1.0);
  const [amount, setAmount] = useState('');
  const [estimatedCredit, setEstimatedCredit] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchRate();
      setAmount('');
      setError('');
      setSuccess(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (amount && !isNaN(amount)) {
      setEstimatedCredit(Math.round(parseFloat(amount) * rate * 100) / 100);
    } else {
      setEstimatedCredit(0);
    }
  }, [amount, rate]);

  const fetchRate = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/user/rnr-conversion-rate`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setRate(data.rate || 1.0);
      }
    } catch (err) {
      console.error('Failed to fetch rate:', err);
    }
  };

  const handleSubmit = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      setError(t('rnrConversionModal.invalidAmount'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/user/convert-rnr`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ amount_rnr: parseFloat(amount) }),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        if (data.error === 'INSUFFICIENT_RNR_BALANCE') {
          setError(t('rnrConversionModal.insufficientRnr'));
        } else if (data.error === 'INVALID_AMOUNT') {
          setError(t('rnrConversionModal.invalidAmount'));
        } else {
          setError(data.message || t('rnrConversionModal.error'));
        }
        return;
      }

      setSuccess(true);
      setAmount('');
      setTimeout(() => {
        if (onSuccess) onSuccess();
        onClose();
      }, 1500);
    } catch (err) {
      console.error('RnR conversion error:', err);
      setError(err.message || t('rnrConversionModal.error'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="border-b px-6 py-4">
          <h2 className="text-xl font-semibold text-gray-900">
            {t('rnrConversionModal.title')}
          </h2>
        </div>

        <div className="p-6 space-y-4">
          {success ? (
            <div className="p-4 bg-green-50 border border-green-200 rounded text-green-700 text-center">
              {t('rnrConversionModal.success')}
            </div>
          ) : (
            <>
              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                  {error}
                </div>
              )}

              <div className="space-y-3">
                <div className="p-3 bg-gray-50 rounded">
                  <p className="text-sm text-gray-600">{t('rnrConversionModal.currentRate')}</p>
                  <p className="text-lg font-bold text-gray-900">
                    1 RnR Point = {rate.toFixed(4)} Credit Points (SP)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('rnrConversionModal.rnrAmount')}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="100.00"
                    disabled={loading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {amount && (
                  <div className="p-3 bg-blue-50 rounded">
                    <p className="text-sm text-gray-600">{t('rnrConversionModal.youWillReceive')}</p>
                    <p className="text-lg font-bold text-blue-600">
                      {estimatedCredit.toFixed(2)} Credit Points (SP)
                    </p>
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <button
                  onClick={onClose}
                  disabled={loading}
                  className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition disabled:opacity-50"
                >
                  {t('rnrConversionModal.cancel')}
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading || !amount || parseFloat(amount) <= 0}
                  className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition disabled:opacity-50"
                >
                  {loading ? t('rnrConversionModal.processing') : t('rnrConversionModal.convert')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
